import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "@fluentui/react";
// Controls
import { Checkbox } from "../checkbox/Checkbox";
import { TextField } from "../../textField/TextField";
import { TextFieldType } from "../../textField/TextField";
import ChecklistItemControl from "./ChecklistItem";
import DndList from "../dndList/DndList";
// Models
import { ChecklistPermissions, isChecklistPermissionDisabled } from "@ms/uno-models/lib/local/client/permissions/ChecklistPermissions";
import { ProgressState } from "@ms/uno-models/lib/local/client/legacyPlanner/progressState/ProgressState";
import { ConvertProgressStateToPercentComplete } from "@ms/uno-models/lib/local/client/legacyPlanner/utilities/ConversionUtilities";
import { ChecklistItem } from "@ms/uno-models/lib/local/client/legacyPlanner/task/ChecklistItem";
import { Task } from "@ms/uno-models/lib/local/client/legacyPlanner/task/Task";
// Styles
import { getClassNames as checklistContainerClassNames, getStyles as checklistContainerStyles } from "./ChecklistContainer.styles";
import { getClassNames as checklistItemClassNames, getStyles as checklistItemStyles } from "./ChecklistItem.styles";
import { reOrderDotsVerticalIconProps } from "../../../icons/icons.styles";
// Utilities
import findIndex from "lodash/findIndex";
import isEmpty from "lodash/isEmpty";
import last from "lodash/last";
import random from "lodash/random";
import trim from "lodash/trim";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { generateOrderHintBetween } from "@ms/uno-utilities/lib/local/OrdinalUtilities";
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
export function ChecklistContainer(props) {
    const { taskId, planId, bucketId, checklistItems, permissions, maxChecklistItemsPerTask, createTask, updateChecklist, updateCompletionRequirementsInfoCallout, containerStyles, checklistItemStyles, showMandatoryChecklistTooltip } = props;
    const theme = useTheme();
    const [state, setState] = useState({
        editedTitle: "",
        itemBeingEdited: "",
        newChecklistItemTitle: "",
        placeholderIndex: null,
        focusedChecklistItemId: null
    });
    const checklistAddItemElement = useRef(null);
    /**
     * Helper method to clear out the auto-focus checklist item once it has been focused
     */ const clearFocusedChecklistItemId = useCallback(()=>{
        setState((prevState)=>({
                ...prevState,
                focusedChecklistItemId: null
            }));
    }, []);
    /**
     * Title for new checklist item is updated
     * @param value Updated title for checklist item
     */ const checklistNewItemTitleChanged = useCallback((value)=>{
        setState((prevState)=>({
                ...prevState,
                newChecklistItemTitle: value
            }));
    }, []);
    useEffect(()=>{
        if (state.focusedChecklistItemId === "addItem" && checklistAddItemElement.current && checklistAddItemElement.current.getElementsByTagName("input")[0]) {
            checklistAddItemElement.current.getElementsByTagName("input")[0].focus();
            clearFocusedChecklistItemId();
        }
    }, [
        state.focusedChecklistItemId,
        clearFocusedChecklistItemId
    ]);
    /**
     * Toggle the isChecked state for checklist item
     * @param itemId Id of checklist item to toggle
     */ const onCheckboxClick = useCallback((itemId)=>{
        const item = checklistItems[itemId];
        if (item !== null && !showMandatoryChecklistTooltip) {
            const updatedChecklist = ChecklistItem.setChecklistProperty(checklistItems, itemId, "isChecked", !item.isChecked);
            updateChecklist(updatedChecklist);
        }
    }, [
        checklistItems,
        updateChecklist,
        showMandatoryChecklistTooltip
    ]);
    /**
     * Update a checklist item's title
     * @param itemId Id of checklist item to update
     * @param newTitle New title for checklist item
     */ const updateItemTitle = useCallback((itemId, newTitle)=>{
        if (!isEmpty(trim(newTitle))) {
            const updatedChecklist = ChecklistItem.setChecklistProperty(checklistItems, itemId, "title", newTitle);
            updateChecklist(updatedChecklist);
        }
    }, [
        checklistItems,
        updateChecklist
    ]);
    /**
     * Add a new item to checklist
     * @param evt UI event on which to add
     */ const addNewItem = useCallback((evt)=>{
        evt.preventDefault();
        const createEnabled = !isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableCreate);
        if (!isEmpty(trim(state.newChecklistItemTitle)) && createEnabled) {
            const itemId = generateChecklistItemUniqueId(checklistItems);
            const updatedChecklist = cloneChecklist(checklistItems);
            const lastChecklistItem = last(ChecklistItem.getSortedChecklistItems(updatedChecklist));
            const newOrderHint = generateOrderHintBetween(null, lastChecklistItem ? lastChecklistItem.orderHint : null);
            const newItem = new ChecklistItem(itemId, false, newOrderHint, state.newChecklistItemTitle);
            updatedChecklist[itemId] = newItem;
            updateChecklist(updatedChecklist);
            checklistNewItemTitleChanged("");
        }
    }, [
        updateChecklist,
        permissions,
        checklistItems,
        state.newChecklistItemTitle,
        checklistNewItemTitleChanged
    ]);
    /**
     * Delete an item from the checklist
     * @param deletedId Id of item to delete
     */ const deleteItem = useCallback((deletedId)=>{
        if (deletedId === "") {
            throw new Error("InvalidArgumentException: deletedId");
        }
        const updatedChecklist = cloneChecklist(checklistItems);
        delete updatedChecklist[deletedId];
        if (!isEmpty(updatedChecklist)) {
            const sortedList = ChecklistItem.getSortedChecklistItems(checklistItems);
            let newFocusIndex = findIndex(sortedList, (item)=>{
                return item.id === deletedId;
            }) + 1;
            if (newFocusIndex > sortedList.length - 1) {
                newFocusIndex = sortedList.length - 2;
            }
            setState((state)=>({
                    ...state,
                    focusedChecklistItemId: sortedList[newFocusIndex].id
                }));
        } else {
            setState((state)=>({
                    ...state,
                    focusedChecklistItemId: "addItem"
                }));
        }
        updateChecklist(updatedChecklist);
    }, [
        checklistItems,
        updateChecklist
    ]);
    /**
     * Promote the checklist item to a task
     * @param itemId
     */ const promoteItem = useCallback((itemId)=>{
        if (itemId === "") {
            throw new Error("InvalidArgumentException: itemId");
        }
        const item = checklistItems[itemId];
        if (item == null) {
            return;
        }
        const task = Task.builder().withPropertyBag({
            id: LocalIdGenerator.generate(),
            title: item.title,
            planId,
            bucketId,
            percentComplete: ConvertProgressStateToPercentComplete(ProgressState.NotStarted)
        }).build();
        createTask(task);
        deleteItem(itemId);
    }, [
        checklistItems,
        planId,
        bucketId,
        createTask,
        deleteItem
    ]);
    /**
     * Move the given checklist item up or down by [move] spots the list, if possible
     */ const moveItem = useCallback((checklistItem, move)=>{
        const sortedList = ChecklistItem.getSortedChecklistItems(checklistItems);
        const oldIndex = findIndex(sortedList, (item)=>{
            return item.id === checklistItem.id;
        });
        if (move < 0 && oldIndex <= 0) {
            return;
        } else if (move > 0 && oldIndex >= sortedList.length - 1) {
            return;
        }
        sortedList.splice(oldIndex, 1);
        const newIndex = oldIndex + move;
        const sortBeforeHint = sortedList[newIndex] ? sortedList[newIndex].orderHint : null;
        const sortAfterHint = sortedList[newIndex - 1] ? sortedList[newIndex - 1].orderHint : null;
        const newOrderHint = generateOrderHintBetween(sortBeforeHint, sortAfterHint);
        const updatedChecklistItem = checklistItem.setProperty("orderHint", newOrderHint);
        const updatedChecklist = cloneChecklist(checklistItems);
        updatedChecklist[updatedChecklistItem.id] = updatedChecklistItem;
        setState((prevState)=>({
                ...prevState,
                focusedChecklistItemId: updatedChecklistItem.id
            }));
        updateChecklist(updatedChecklist);
    }, [
        checklistItems,
        updateChecklist
    ]);
    /**
     * Render a single checklist item
     * @param checklistItem Item to render
     * @param index Index of this item in the list
     * @param itemType Item type of the parent DndList component. This render function should make use of this itemType to avoid render errors
     * @param checklistItemCount Number of items in the list
     * @param isReorderDisabled Can this item be re-ordered or not
     */ const renderChecklistItem = (checklistItem, index, itemType, checklistItems, isReorderDisabled)=>{
        return /*#__PURE__*/ React.createElement(ChecklistItemControl, {
            key: `checklistItem-${taskId}-${checklistItem.id}-${index}`,
            listItem: checklistItem,
            itemTypeString: itemType,
            index: index,
            isReorderDisabled: isReorderDisabled,
            permissions: permissions,
            onPromote: promoteItem,
            onMove: moveItem,
            onDelete: deleteItem,
            onRename: updateItemTitle,
            onCheckboxClick: onCheckboxClick,
            setFocusOnInit: state.focusedChecklistItemId === checklistItem.id,
            clearFocusedChecklistItemId: clearFocusedChecklistItemId,
            theme: theme,
            gripperIconProps: reOrderDotsVerticalIconProps
        });
    };
    /**
     * Render the placeholder item for the list
     */ const renderNewItem = ()=>{
        const isCreateDisabled = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableCreate);
        const checklistItemClassNames = getChecklistItemClassNames(theme, permissions, checklistItemStyles);
        const newChecklistItem = Object.keys(checklistItems).length >= maxChecklistItemsPerTask ? null : /*#__PURE__*/ React.createElement("div", {
            className: `${checklistItemClassNames.root} new`,
            key: "checklistNewItem"
        }, /*#__PURE__*/ React.createElement("div", {
            className: checklistItemClassNames.listItemContents
        }, /*#__PURE__*/ React.createElement(Checkbox, {
            className: "checkbox",
            title: Strings.Checklist_ChecklistCheckboxTitle,
            checked: false,
            inputProps: {
                tabIndex: -1
            },
            disabled: isCreateDisabled,
            appTheme: theme
        }), /*#__PURE__*/ React.createElement("form", {
            onSubmit: addNewItem,
            autoComplete: "off",
            ref: checklistAddItemElement
        }, /*#__PURE__*/ React.createElement(TextField, {
            className: "checklistTextField",
            title: isCreateDisabled ? Strings.SharedComponents_ReadonlyFieldPermissionsTooltip : undefined,
            tabIndex: 0,
            placeholder: Strings.Checklist_NewChecklistItem,
            onChange: (event, newValue)=>{
                checklistNewItemTitleChanged(newValue);
            },
            value: state.newChecklistItemTitle,
            onBlur: addNewItem,
            styleType: TextFieldType.Text,
            maxLength: ChecklistItem.TitleMaxLength,
            ariaLabel: Strings.Checklist_NewChecklistItemAriaLabel,
            disabled: isCreateDisabled,
            theme: theme
        }))));
        return newChecklistItem;
    };
    const sortedChecklistItems = ChecklistItem.getSortedChecklistItems(checklistItems);
    const containerClassNames = getChecklistContainerClassNames(theme, containerStyles);
    const showToolTip = showMandatoryChecklistTooltip ? updateCompletionRequirementsInfoCallout : undefined;
    return /*#__PURE__*/ React.createElement("div", {
        className: containerClassNames.root,
        onClick: showToolTip
    }, /*#__PURE__*/ React.createElement(DndList, {
        listItems: sortedChecklistItems,
        onRenderItem: renderChecklistItem,
        isReorderDisabled: isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableReorder),
        onReorder: moveItem,
        itemType: "checklistItem",
        ariaLabel: getChecklistArialabel(permissions)
    }), renderNewItem());
}
/**
 * Based on the current permissions, generate screen reader text for the checklist
 */ function getChecklistArialabel(permissions) {
    const isCheckDisabled = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableCheckedStateToggling);
    const isEditTitleDisabled = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableEditTitle);
    const isPromoteDisabled = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisablePromote);
    const isDeleteDisabled = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableDelete);
    const isReorderDisabled = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableReorder);
    const checklistItemInstructionsArray = [];
    if (!isCheckDisabled) {
        checklistItemInstructionsArray.push(Strings.Checklist_ChecklistCheckInstructions);
    }
    if (!isEditTitleDisabled) {
        checklistItemInstructionsArray.push(Strings.Checklist_ChecklistEditNameInstructions);
    }
    if (!isDeleteDisabled) {
        checklistItemInstructionsArray.push(Strings.Checklist_ChecklistDeleteInstructions);
    }
    if (!isDeleteDisabled || !isPromoteDisabled) {
        checklistItemInstructionsArray.push(Strings.Checklist_ChecklistDeleteOrPromoteInstructions);
    }
    if (!isReorderDisabled) {
        checklistItemInstructionsArray.push(Strings.Checklist_ChecklistReorderInstructions);
    }
    return checklistItemInstructionsArray.join(Strings.Checklist_ChecklistInstructionsListDelimiter);
}
/**
 * Get the classnames associated with the computed styles for the checklist container component
 */ const getChecklistContainerClassNames = (theme, containerStyles)=>{
    return checklistContainerClassNames(checklistContainerStyles(theme, containerStyles));
};
/**
 * Get the classnames associated with the computed styles for the checklist item component
 */ const getChecklistItemClassNames = (theme, permissions, customStyles)=>{
    return checklistItemClassNames(checklistItemStyles(theme, customStyles, isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableCreate)));
};
/**
 * Clone the checklist passed in via the prop.
 */ const cloneChecklist = (checklistItems)=>{
    return ChecklistItem.cloneChecklist(checklistItems);
};
/**
 * Generate a checklist item id for a new checklist item
 */ const generateChecklistItemUniqueId = (checklistItems)=>{
    const maxId = 99999;
    const minId = 10000;
    let id = "";
    const allChecklistItems = checklistItems ?? [];
    do {
        id = random(minId, maxId, false).toString();
    }while (allChecklistItems[id] != null)
    return id;
};
